import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ExternalLink, Bot, FileText, Calculator, BadgeCheck, Gauge, IdCard, ListOrdered, MessagesSquare, ClipboardCheck, Mic, MessageCircle, Target, LayoutDashboard, Network, Zap } from "lucide-react";
import { TOOLS, TOOL_CATEGORIES } from "../../../data/workforceOsTools";
import { SUBMIT_RESUME_URL } from "../../../data/recruitingLinks";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";

const ICON_MAP = {
  Bot, FileText, Calculator, BadgeCheck, Gauge, IdCard, ListOrdered, MessagesSquare,
  ClipboardCheck, Mic, MessageCircle, Target, LayoutDashboard, Network, Zap, Sparkles,
};

const STATUS_STYLES = {
  "Free Tool":   "bg-emerald-100 text-emerald-800 border-emerald-200",
  "AI-Ready":    "bg-indigo-100 text-indigo-800 border-indigo-200",
  "MVP":         "bg-amber-100 text-amber-800 border-amber-200",
  "Coming Soon": "bg-slate-100 text-slate-700 border-slate-200",
};

function ToolCard({ tool }) {
  const Icon = ICON_MAP[tool.icon] || Sparkles;
  return (
    <Link
      to={`/workforce-os/${tool.slug}`}
      className="group relative flex flex-col h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-trital-navy hover:shadow-[0_18px_40px_-15px_rgba(15,23,42,0.18)] transition-all"
      data-testid={`wos-tool-card-${tool.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <span
          className={`text-[10.5px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${
            STATUS_STYLES[tool.status] || STATUS_STYLES["MVP"]
          }`}
        >
          {tool.status}
        </span>
      </div>
      <h3 className="mt-4 font-display font-bold text-[17px] text-trital-navy leading-snug">
        {tool.name}
      </h3>
      <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed flex-1">{tool.tagline}</p>
      <div className="mt-4 flex items-center justify-between text-[12px] text-slate-500">
        <span>For {tool.audience}</span>
        <span className="inline-flex items-center gap-1 font-semibold text-trital-navy group-hover:gap-1.5 transition-all">
          {tool.cta} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
        </span>
      </div>
    </Link>
  );
}

function CategorySection({ category, tools }) {
  return (
    <section
      id={category.key}
      className="py-14 lg:py-20 border-t border-slate-100"
      data-testid={`wos-section-${category.key}`}
    >
      <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
        <div className="max-w-[680px]">
          <div className="overline text-trital-red">{category.eyebrow}</div>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight text-trital-navy leading-[1.1]">
            {category.headline}
          </h2>
          <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{category.description}</p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map((t) => (
            <ToolCard key={t.slug} tool={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function WorkforceOsHome() {
  // SEO
  React.useEffect(() => {
    document.title = "TRITAL Workforce OS™ | Free AI Healthcare Recruiting Tools";
    const desc =
      "Use TRITAL Workforce OS™ free healthcare recruiting tools: AI nurse recruiter chat, resume formatting, candidate engagement, RateLab pay calculator, submittal scoring, MSP readiness, clinician passports, and healthcare workforce matching.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  return (
    <WorkforceOsLayout
      eyebrow="TRITAL Workforce OS™"
      title="Free AI-Ready Healthcare Recruiting Tools"
      description="Use free healthcare recruiting tools for resume formatting, candidate screening, job-fit guidance, pay-package calculations, submittal readiness, MSP/VMS workflows, and AI-powered candidate engagement."
      showAiDisclaimer={true}
    >
      {/* Hero CTA cluster */}
      <section className="py-10 lg:py-14" data-testid="wos-hero-ctas">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 flex flex-wrap gap-3">
          <a
            href="#tools"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift"
            data-testid="wos-cta-use-tools"
          >
            Use Free Tools <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </a>
          <Link
            to="/workforce-os/ai-nurse-recruiter"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white font-semibold btn-lift"
            data-testid="wos-cta-chat-rebeca"
          >
            <Bot className="w-4 h-4" strokeWidth={1.8} /> Chat with AI Nurse Recruiter
          </Link>
          <Link
            to="/careers/jobs"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy font-semibold btn-lift"
            data-testid="wos-cta-search-jobs"
          >
            Search Healthcare Jobs
          </Link>
          <a
            href={SUBMIT_RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy font-semibold btn-lift"
            data-testid="wos-cta-submit-resume"
          >
            Submit Resume <ExternalLink className="w-3.5 h-3.5 opacity-70" strokeWidth={1.8} />
          </a>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="py-10 lg:py-14 bg-trital-navy text-white" data-testid="wos-platform-overview">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="overline text-white/70">Platform Overview</div>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight leading-[1.1]">
              The AI-ready healthcare recruiting operating system.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed max-w-[60ch]">
              TRITAL Workforce OS™ brings together nurse recruiting, resume intelligence, candidate
              engagement, pay-package intelligence, MSP/VMS submission readiness, recruiter copilots,
              clinician passport profiles, and workforce matching — into one fast, free, and
              recruiter-grade system.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Free Tools",   value: "10+" },
              { label: "AI Agents",    value: "4" },
              { label: "Specialties",  value: "50+" },
              { label: "States Covered", value: "All 50" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 p-5">
                <div className="font-display font-black text-3xl tabular-nums">{s.value}</div>
                <div className="text-[12px] text-white/70 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools by category */}
      <div id="tools" />
      {TOOL_CATEGORIES.map((cat) => (
        <CategorySection
          key={cat.key}
          category={cat}
          tools={TOOLS.filter((t) => t.category === cat.key)}
        />
      ))}

      {/* For Recruiters / Clinicians / Employers */}
      <section className="py-14 lg:py-20 bg-slate-50 border-t border-slate-100" data-testid="wos-audience-blocks">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              title: "For Recruiters",
              body: "Score every submittal before it leaves your inbox. Generate recruiter-ready candidate summaries in seconds. Talk to a copilot that knows healthcare staffing.",
              cta: { label: "Open Recruiter Tools", to: "/workforce-os/recruiter-dashboard" },
            },
            {
              title: "For Clinicians",
              body: "Build a free clinician passport, format your resume with healthcare-aware templates, and chat with Rebeca to find your next opportunity.",
              cta: { label: "Tools for Clinicians", to: "/workforce-os/clinician-passport" },
            },
            {
              title: "For Healthcare Employers",
              body: "Connect your MSP/VMS workflows to a workforce platform that's compliance-first, AI-ready, and integrated with TRITAL ShiftBridge™ for PRN.",
              cta: { label: "Talk to TRITAL Care", to: "/contact" },
            },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col">
              <h3 className="font-display font-bold text-xl text-trital-navy">{b.title}</h3>
              <p className="mt-3 text-[14px] text-slate-600 leading-relaxed flex-1">{b.body}</p>
              <Link
                to={b.cta.to}
                className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-trital-navy hover:gap-2 transition-all"
                data-testid={`wos-audience-cta-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {b.cta.label} <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20" data-testid="wos-final-cta">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-trital-navy tracking-tight leading-[1.1]">
            Talk to Rebeca, find your next role, or score a submittal — free.
          </h2>
          <p className="mt-3 text-slate-600 max-w-[60ch] mx-auto">
            Every tool above is free. No signup wall, no paywall. Recruiters and clinicians can
            start in seconds.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link
              to="/workforce-os/ai-nurse-recruiter"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift"
              data-testid="wos-final-cta-rebeca"
            >
              <Bot className="w-4 h-4" /> Chat with Rebeca
            </Link>
            <Link
              to="/workforce-os/ratelab"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-trital-red text-white font-semibold btn-lift"
              data-testid="wos-final-cta-ratelab"
            >
              <Calculator className="w-4 h-4" /> Run RateLab
            </Link>
            <Link
              to="/careers/jobs"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-300 text-trital-navy font-semibold btn-lift"
              data-testid="wos-final-cta-jobs"
            >
              Search Jobs
            </Link>
          </div>
        </div>
      </section>

      <RebecaMiguelChatbot variant="floating" />
    </WorkforceOsLayout>
  );
}
